/*
 * Copyright 2026 Sony Corporation
 */
import { GATE_LABELS, type Gate, type RunSummary } from '@ips-jit/schema';
import {
  Alert,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import { failuresOf, outcomeOf, summarize, type Outcome, type Summary } from '../services/dashboard';

const PERIODS = [7, 30, 90] as const;
type Period = (typeof PERIODS)[number];

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

export const gateLabel = (gate: string): string => (GATE_LABELS as Record<string, string>)[gate] ?? gate;

const OUTCOME_LABELS: Record<Outcome, string> = {
  delivered: 'delivered',
  built: 'built, not delivered',
  answered: 'answered',
  failed: 'failed',
  rejected: 'rejected',
  superseded: 'superseded',
  open: 'in progress',
};

const OUTCOME_COLORS: Record<Outcome, 'success' | 'info' | 'default' | 'error' | 'warning'> = {
  delivered: 'success',
  built: 'info',
  answered: 'default',
  failed: 'error',
  rejected: 'warning',
  superseded: 'default',
  open: 'info',
};

const percent = (part: number, whole: number): string => (whole === 0 ? '–' : `${Math.round((part / whole) * 100)}%`);
const seconds = (ms?: number): string => (ms === undefined ? '–' : `${Math.round(ms / 1000)} s`);
const minutes = (ms?: number): string => (ms === undefined ? '–' : `${(ms / 60_000).toFixed(1)} min`);
const thousands = (n?: number): string => (n === undefined ? '–' : `${Math.round(n / 1000)}k`);
const count = (n?: number): string => (n === undefined ? '–' : String(n));

const Figure = ({ label, value, detail }: { label: string; value: string; detail?: string }) => (
  <Paper sx={{ p: 2 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5">{value}</Typography>
    {detail && (
      <Typography variant="caption" color="text.secondary">
        {detail}
      </Typography>
    )}
  </Paper>
);

/** Failures by gate, as a bar per gate: where the loop loses the most is what to fix next. */
const GateBars = ({ summary }: { summary: Summary }) => {
  const most = summary.gates[0]?.count ?? 0;
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1">Where the work was turned back</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Each time a gate (review, verification, delivery, the device) sent a request back for another round. A run can
        be counted at several gates.
      </Typography>
      {summary.gates.length === 0 ? (
        <Typography color="text.secondary">Nothing was turned back in this period.</Typography>
      ) : (
        <Stack spacing={0.5} data-testid="gate-bars">
          {summary.gates.map(({ gate, count: n, runs }) => (
            <Stack key={gate} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ width: 140, flexShrink: 0 }}>
                {gateLabel(gate)}
              </Typography>
              <Box sx={{ flex: 1, bgcolor: 'action.hover', borderRadius: 1, height: 18 }}>
                <Box
                  sx={{
                    width: `${(n / most) * 100}%`,
                    bgcolor: gate === 'runtime' || gate === 'assistant' ? 'error.main' : 'primary.main',
                    height: '100%',
                    borderRadius: 1,
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ width: 130, flexShrink: 0 }}>
                {n} ({runs} run{runs === 1 ? '' : 's'}, {percent(runs, summary.built)})
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

/** Every run of the period, newest first, in a panel that scrolls on its own next to the summary. */
const RecentRuns = ({ runs }: { runs: RunSummary[] }) => (
  <Paper
    component="section"
    aria-label="Recent runs"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      position: { md: 'sticky' },
      top: { md: 0 },
      maxHeight: { md: 'calc(100dvh - 140px)' },
    }}
  >
    <Stack direction="row" sx={{ p: 2, pb: 1, alignItems: 'baseline' }}>
      <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
        Recent runs
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {runs.length} run{runs.length === 1 ? '' : 's'}
      </Typography>
    </Stack>
    <Divider />
    {runs.length === 0 ? (
      <Typography sx={{ p: 2 }} color="text.secondary">
        No runs in this period.
      </Typography>
    ) : (
      <List dense disablePadding sx={{ overflowY: 'auto', minHeight: 0 }} data-testid="recent-runs">
        {runs.map((run) => {
          const outcome = outcomeOf(run);
          const m = run.metrics;
          const failures = failuresOf(run);
          const time =
            m.implement_ms === undefined && m.plan_ms === undefined
              ? undefined
              : (m.plan_ms ?? 0) + (m.implement_ms ?? 0);
          return (
            <ListItem key={`${run.project_id}:${run.run_id}`} divider sx={{ display: 'block', py: 1.25 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Chip size="small" color={OUTCOME_COLORS[outcome]} label={OUTCOME_LABELS[outcome]} />
                <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                  {run.source === 'device_error' ? 'automatic fix' : 'user'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {new Date(run.created_at).toLocaleString()}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                {count(m.builds)} build{m.builds === 1 ? '' : 's'} · {minutes(time)} ·{' '}
                {m.input_tokens === undefined
                  ? '– tokens'
                  : `${thousands(m.input_tokens)} / ${thousands(m.output_tokens)} tokens`}
              </Typography>
              {failures.length > 0 && (
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {failures.map(({ gate, count: n }) => (
                    <Chip
                      key={gate}
                      size="small"
                      variant="outlined"
                      label={n > 1 ? `${gateLabel(gate)} ×${n}` : gateLabel(gate)}
                    />
                  ))}
                </Stack>
              )}
            </ListItem>
          );
        })}
      </List>
    )}
  </Paper>
);

/** The numbers of the generator across every project of this deployment: how often it succeeds, and where it fails. */
export const Dashboard = () => {
  const { api } = useAuth();
  const [period, setPeriod] = useState<Period>(30);
  const [runs, setRuns] = useState<RunSummary[]>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setRuns(undefined);
    api
      .listRunMetrics(period)
      .then((result) => {
        if (!cancelled) {
          setRuns(result.runs);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(describe(caught, 'Could not load the runs.'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, period]);

  const summary = runs ? summarize(runs) : undefined;

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 280 }}>
          Every run of the assistant in this deployment, across all projects and users. A run is one request: a plan,
          the build and the delivery to the device, or an automatic fix of an error the device reported.
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={(_event, value: Period | null) => value && setPeriod(value)}
          aria-label="Period"
        >
          {PERIODS.map((days) => (
            <ToggleButton key={days} value={days}>
              {days} days
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}

      {summary === undefined ? (
        !error && <Typography color="text.secondary">Loading...</Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(320px, 2fr)' },
            alignItems: 'start',
          }}
        >
          <Stack spacing={2} component="section" aria-label="Summary" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
              }}
            >
              <Figure
                label="Requests"
                value={String(summary.requests)}
                detail={`${summary.autoFixes} automatic fixes`}
              />
              <Figure
                label="Delivered"
                value={percent(summary.delivered, summary.built)}
                detail={`${summary.delivered} of ${summary.built} runs that built`}
              />
              <Figure
                label="First pass"
                value={percent(summary.firstPass, summary.built)}
                detail="one build, nothing turned back"
              />
              <Figure label="Time per request" value={minutes(summary.medianTotalMs)} detail="median, plan and build" />
              <Figure
                label="Builds per request"
                value={count(summary.medianBuilds)}
                detail={`median; build wait ${seconds(summary.medianBuildMs)}`}
              />
              <Figure
                label="Tokens per request"
                value={`${thousands(summary.medianTokensIn)} / ${thousands(summary.medianTokensOut)}`}
                detail="median, in / out"
              />
              <Figure
                label="Cost per run"
                value={summary.medianCostUsd === undefined ? '–' : `$${summary.medianCostUsd.toFixed(2)}`}
                detail={`median; $${summary.totalCostUsd.toFixed(2)} over ${summary.costedRuns} runs, list price`}
              />
            </Box>

            <GateBars summary={summary} />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Paper sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle1">Outcomes</Typography>
                <Table size="small">
                  <TableBody>
                    {(Object.keys(OUTCOME_LABELS) as Outcome[]).map((outcome) => (
                      <TableRow key={outcome}>
                        <TableCell>{OUTCOME_LABELS[outcome]}</TableCell>
                        <TableCell align="right">{summary.outcomes[outcome]}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
              <Paper sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle1">Automatic fixes</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Fix held on the device</TableCell>
                      <TableCell align="right">{summary.autoFix.held}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Error came back ({gateLabel('runtime' satisfies Gate)})</TableCell>
                      <TableCell align="right">{summary.autoFix.cameBack}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Cause not in the code</TableCell>
                      <TableCell align="right">{summary.autoFix.notCode}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Failed</TableCell>
                      <TableCell align="right">{summary.autoFix.failed}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </Stack>
          </Stack>

          <RecentRuns runs={runs ?? []} />
        </Box>
      )}
    </Stack>
  );
};
